/**
 * Agent Orchestration API service — create/update/delete agents and folders.
 *
 * Extracted from contexts/ApiContext.tsx so finbot's services/api/agent-api.ts
 * matches the cluster-C fork layout (citeka, skhcn, sdl). The export shape is
 * unchanged: ApiContext still instantiates this class and exposes it via
 * useAgentApi().
 */

/** Get the Python backend URL from environment variables */
export function getPythonBackendUrl(): string {
  return process.env.REACT_APP_PYTHON_BACKEND_URL || '';
}

export class EnhancedAgentApiService {
  private baseUrl: string;
  private getAccessToken: () => string | undefined;

  constructor(getAccessToken: () => string | undefined) {
    this.baseUrl = getPythonBackendUrl();
    this.getAccessToken = getAccessToken;
  }

  private async fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    const token = this.getAccessToken();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers
      }
    });

    if (!response.ok) {
      let errorMessage = `API call failed: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorData.error || errorMessage;
      } catch {
        const errorText = await response.text();
        errorMessage = errorText || errorMessage;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async createAgent(payload: object): Promise<Record<string, unknown>> {
    return this.fetchWithAuth('/api/orchestration/agents', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async updateAgent(agentId: string, payload: object): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/api/orchestration/agents/${agentId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  async deleteAgent(agentId: string): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/api/orchestration/agents/${agentId}`, {
      method: 'DELETE'
    });
  }

  async deleteFolder(folderId: string, userId: string): Promise<Record<string, unknown>> {
    return this.fetchWithAuth('/api/orchestration/folders', {
      method: 'DELETE',
      body: JSON.stringify({ folder_id: folderId, user_id: userId })
    });
  }
}
